import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useAuth } from "../lib/auth";
import { Button, ModePill } from "../components/ui";

/**
 * The application shell.
 *
 * The navigation is styled as the dark bound spine of a ledger, holding the warm
 * paper working surface. It is the one piece of heavy tone in the interface, which
 * is what lets everything else stay quiet.
 */

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

/* Icons are inline 16px strokes rather than an icon package: eight glyphs do not
   justify a dependency, and drawing them here keeps the stroke weight consistent. */
const icon = (path: ReactNode) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="shrink-0">
    {path}
  </svg>
);

const Icons = {
  overview: icon(
    <>
      <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
    </>,
  ),
  payments: icon(
    <>
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1.5 6.5h13" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4 10h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  payouts: icon(
    <>
      <path d="M8 2v9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M4.5 7.5 8 11l3.5-3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.5 13.5h11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  keys: icon(
    <>
      <circle cx="5" cy="8" r="3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 8h6M12 8v2.5M14 8v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  webhooks: icon(
    <>
      <circle cx="4" cy="11.5" r="2" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="12" cy="11.5" r="2" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="8" cy="4" r="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M6.7 5.7 4.9 9.6M9.3 5.7l1.8 3.9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  kyc: icon(
    <>
      <rect x="2.5" y="2" width="11" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 6h5M5.5 8.5h5M5.5 11h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  pricing: icon(
    <>
      <path d="M8 1.5v13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M11 4.5H6.5a2 2 0 0 0 0 4h3a2 2 0 0 1 0 4H5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  tenants: icon(
    <>
      <path d="M2.5 13.5V5l4-2.5V13.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M6.5 13.5V6l7 2v5.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M1.5 13.5h13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  ledger: icon(
    <>
      <path d="M2 13.5V2.5M14 13.5V2.5M2 8h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M2 13.5h12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  audit: icon(
    <>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  link: icon(
    <>
      <path d="M6.5 9.5a3 3 0 0 0 4.2 0l2.1-2.1a3 3 0 0 0-4.2-4.2l-1 1"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M9.5 6.5a3 3 0 0 0-4.2 0L3.2 8.6a3 3 0 0 0 4.2 4.2l1-1"
            stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  invoice: icon(
    <>
      <path d="M3.5 2.5h9v11l-2-1.2-2 1.2-2-1.2-2 1.2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M6 6h4M6 8.5h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
  recurring: icon(
    <>
      <path d="M13 8a5 5 0 1 1-1.6-3.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M13.5 2v3h-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </>,
  ),
  shield: icon(
    <>
      <path d="M8 1.8 13 3.5v4.2c0 3-2 5.3-5 6.5-3-1.2-5-3.5-5-6.5V3.5z"
            stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="m5.8 7.8 1.6 1.6 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </>,
  ),
  team: icon(
    <>
      <circle cx="6" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1.5 13c0-2.2 2-4 4.5-4s4.5 1.8 4.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M11 4.2a2.2 2.2 0 0 1 0 4.3M12 9.3c1.6.5 2.5 1.9 2.5 3.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </>,
  ),
};

const MERCHANT_NAV: NavSection[] = [
  {
    label: "Money",
    items: [
      { to: "/", label: "Overview", icon: Icons.overview },
      { to: "/payments", label: "Payments", icon: Icons.payments },
      { to: "/payouts", label: "Payouts", icon: Icons.payouts },
    ],
  },
  {
    label: "Billing",
    items: [
      { to: "/links", label: "Payment links", icon: Icons.link },
      { to: "/invoices", label: "Invoices", icon: Icons.invoice },
      { to: "/subscriptions", label: "Subscriptions", icon: Icons.recurring },
      { to: "/customers", label: "Customers", icon: Icons.team },
    ],
  },
  {
    label: "Integration",
    items: [
      { to: "/keys", label: "API keys", icon: Icons.keys },
      { to: "/webhooks", label: "Webhooks", icon: Icons.webhooks },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/verification", label: "Verification", icon: Icons.kyc },
      { to: "/fraud", label: "Fraud controls", icon: Icons.shield },
      { to: "/pricing", label: "Pricing", icon: Icons.pricing },
      { to: "/team", label: "Team", icon: Icons.team },
    ],
  },
];

const ADMIN_NAV: NavSection[] = [
  {
    label: "Operations",
    items: [
      { to: "/admin", label: "Overview", icon: Icons.overview },
      { to: "/admin/verification", label: "Verification queue", icon: Icons.kyc },
      { to: "/admin/tenants", label: "Client firms", icon: Icons.tenants },
      { to: "/admin/payouts", label: "Payout runs", icon: Icons.payouts },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/admin/pricing", label: "Pricing plans", icon: Icons.pricing },
      { to: "/admin/ledger", label: "Ledger", icon: Icons.ledger },
      { to: "/admin/audit", label: "Audit log", icon: Icons.audit },
    ],
  },
];

export function AppShell() {
  const { user, tenant, isOperator, mode, setMode, signOut } = useAuth();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Navigating on a phone must close the drawer, or the destination is hidden
  // behind the menu the user just used.
  useEffect(() => setNavOpen(false), [location.pathname]);

  const sections = isOperator ? ADMIN_NAV : MERCHANT_NAV;

  return (
    <div className="flex min-h-screen">
      {/* The spine */}
      <aside
        className={[
          "fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col bg-ink text-paper",
          "transition-transform duration-200 lg:translate-x-0",
          navOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        {/* A brass hairline down the outer edge, like the gilt on a bound ledger. */}
        <span
          className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-brass to-transparent opacity-45"
          aria-hidden="true"
        />

        <div className="px-5 pb-5 pt-6">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-[22px] leading-none text-paper">PayFlow</span>
            <span className="h-1 w-1 rounded-full bg-brass" aria-hidden="true" />
          </div>
          <p className="mt-1 text-[10.5px] uppercase tracking-[0.14em] text-paper/40">
            Payments · Bushveld
          </p>
        </div>

        {!isOperator && (
          <div className="mx-5 mb-5">
            <ModeSwitch mode={mode} onChange={setMode} canGoLive={Boolean(tenant?.can_transact_live)} />
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          {sections.map((section) => (
            <div key={section.label} className="mb-5">
              <p className="px-2 pb-1.5 text-[9.5px] font-semibold uppercase tracking-[0.13em] text-paper/35">
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === "/" || item.to === "/admin"}
                      className={({ isActive }) =>
                        [
                          "group relative flex items-center gap-2.5 rounded-md px-2 py-[7px] text-[13px] transition-colors duration-150",
                          isActive
                            ? "bg-paper/10 text-paper font-medium"
                            : "text-paper/60 hover:bg-paper/[0.06] hover:text-paper/90",
                        ].join(" ")
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {/* The active marker is a brass rule at the item's edge —
                              a ledger's marginal tick, not a filled pill. */}
                          <span
                            className={[
                              "absolute -left-3 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-r bg-brass transition-opacity duration-150",
                              isActive ? "opacity-100" : "opacity-0",
                            ].join(" ")}
                            aria-hidden="true"
                          />
                          {item.icon}
                          <span>{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-paper/10 px-4 py-4">
          <p className="truncate text-[12.5px] font-medium text-paper/90">{user?.full_name}</p>
          <p className="truncate text-[11.5px] text-paper/45">{user?.email}</p>
          {tenant && (
            <p className="mt-1.5 truncate text-[11px] text-paper/40">{tenant.legal_name}</p>
          )}
          <button
            onClick={signOut}
            className="mt-3 text-[12px] text-paper/50 underline-offset-2 transition-colors hover:text-paper hover:underline"
          >
            Sign out
          </button>
        </div>
      </aside>

      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink/50 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* The working surface */}
      <div className="flex min-w-0 flex-1 flex-col lg:ml-[248px]">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-rule bg-paper/85 px-4 py-3 backdrop-blur-md lg:hidden">
          <Button size="sm" variant="ghost" onClick={() => setNavOpen(true)} aria-label="Open navigation">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path d="M2.5 5h13M2.5 9h13M2.5 13h13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </Button>
          <span className="font-display text-[17px]">PayFlow</span>
          {!isOperator && <ModePill mode={mode} />}
        </header>

        <main className="mx-auto w-full max-w-[1180px] flex-1 px-4 py-7 sm:px-7 sm:py-9">
          <Outlet />
        </main>

        <footer className="mx-auto w-full max-w-[1180px] px-4 pb-8 sm:px-7">
          <div className="rule-t pt-4 text-[11.5px] text-ink-faint">
            PayFlow — a product of{" "}
            <a
              href="https://bushveldtech.com"
              className="underline underline-offset-2 transition-colors hover:text-ink-muted"
            >
              Bushveld
            </a>
              .
          </div>
        </footer>
      </div>
    </div>
  );
}

/** ModeSwitch toggles between sandbox and live data.
 *
 *  The live half is disabled until the account is approved, and says why: a merchant
 *  finding the switch inert with no explanation will open a support ticket. */
function ModeSwitch({
  mode,
  onChange,
  canGoLive,
}: {
  mode: string;
  onChange: (mode: "sandbox" | "live") => void;
  canGoLive: boolean;
}) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-0.5 rounded-md bg-paper/[0.07] p-0.5">
        <button
          onClick={() => onChange("sandbox")}
          aria-pressed={mode === "sandbox"}
          className={[
            "rounded-[3px] px-2 py-1.5 text-[11.5px] font-medium transition-colors duration-150",
            mode === "sandbox"
              ? "bg-brass text-ink"
              : "text-paper/55 hover:text-paper/85",
          ].join(" ")}
        >
          Sandbox
        </button>
        <button
          onClick={() => canGoLive && onChange("live")}
          disabled={!canGoLive}
          aria-pressed={mode === "live"}
          title={canGoLive ? undefined : "Complete verification to transact live"}
          className={[
            "rounded-[3px] px-2 py-1.5 text-[11.5px] font-medium transition-colors duration-150",
            mode === "live"
              ? "bg-verdigris-bright text-paper"
              : "text-paper/55 hover:text-paper/85",
            !canGoLive && "cursor-not-allowed opacity-35 hover:text-paper/55",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          Live
        </button>
      </div>
      {!canGoLive && (
        <p className="mt-1.5 text-[10.5px] leading-snug text-paper/35">
          Complete verification to switch to live.
        </p>
      )}
    </div>
  );
}
